"use client";

import NextLink from "next/link";
import { usePathname } from "next/navigation";

import { getCurrentJwt } from "#app/(unauthorized)/authentication/jwt";
import { Segment } from "#app/(unauthorized)/authentication/segment";
import { HeaderWithSlots } from "#ui/header";
import { AnchorButton, LinkButton } from "#ui/link";
import { Logo } from "#ui/logo";
import { NavSheet } from "#ui/nav";
import { NavAccordion } from "#ui/nav-accordion";
import { NavMenu } from "#ui/nav-menu";

import { SymbolSearch } from "../market-research/search-suggester";
import { LinkDocumentsReceived } from "./link-documents-received";
import { PopoverProfileAndSettings } from "./popover-profile-and-settings";
import { useNavRoutes } from "./use-nav-routes";

export function Header() {
  const { segment } = getCurrentJwt();
  const isInstitutionalUser = segment === Segment.CorporateEntities;

  const activePath = usePathname();
  const routes = useNavRoutes();

  return (
    <>
      <HeaderWithSlots
        layout="mobileOnly"
        className="xl:hidden"
        slot0={
          <NextLink href={routes.Home.path!}>
            {/* TODO: Convert dual logos to one responsive logo */}
            <Logo layout="mark-only" size="xs" className="sm:hidden" />
            <Logo layout="mark-and-word" size="xs" className="max-sm:hidden" />
          </NextLink>
        }
        slot1={
          <NavSheet
            key={activePath} // Force remount to close sheet on route change
            elementMain={
              <nav
                aria-label="Primary Navigation"
                className="divide-y divide-shade-15"
              >
                <NavAccordion
                  activePath={activePath}
                  routes={routes.mobileOnly}
                  collapsible
                />
                <div className="flex flex-col gap-10px p-20px">
                  {routes.MyTeam && (
                    <LinkButton href={routes.MyTeam.path!}>
                      {routes.MyTeam.element}
                    </LinkButton>
                  )}
                  <AnchorButton
                    href={routes.SignOut.path}
                    variant="outline"
                    palette="neutral"
                  >
                    {routes.SignOut.element}
                  </AnchorButton>
                </div>
              </nav>
            }
            elementFooter={
              isInstitutionalUser ? undefined : (
                <nav aria-label="Secondary Navigation" className="p-20px">
                  <SymbolSearch isAuthed />
                </nav>
              )
            }
          />
        }
        slot2={
          routes.DocumentsReceived || routes.ProfileAndSettings ? (
            <nav
              aria-label="Secondary Navigation"
              className="flex items-center gap-10px"
            >
              {routes.DocumentsReceived ? (
                <LinkDocumentsReceived route={routes.DocumentsReceived} />
              ) : null}
              {routes.ProfileAndSettings ? (
                <PopoverProfileAndSettings
                  activePath={activePath}
                  route={routes.ProfileAndSettings}
                />
              ) : null}
            </nav>
          ) : undefined
        }
      />
      <HeaderWithSlots
        layout="desktopOnly"
        className="max-xl:hidden xl:grid"
        slot0={
          <NextLink href={routes.Home.path!}>
            <Logo layout="mark-and-word" size="sm" />
          </NextLink>
        }
        slot1={
          <NavMenu
            aria-label="Primary Navigation"
            activePath={activePath}
            routes={routes.desktopOnly}
          />
        }
        slot2={
          <nav
            aria-label="Secondary Navigation"
            className="flex items-center gap-20px"
          >
            {isInstitutionalUser ? null : (
              <div className="w-200px">
                <SymbolSearch isAuthed />
              </div>
            )}
            {routes.DocumentsReceived || routes.ProfileAndSettings ? (
              <span className="flex items-center gap-10px">
                {routes.DocumentsReceived ? (
                  <LinkDocumentsReceived route={routes.DocumentsReceived} />
                ) : null}
                {routes.ProfileAndSettings ? (
                  <PopoverProfileAndSettings
                    activePath={activePath}
                    route={routes.ProfileAndSettings}
                  />
                ) : null}
              </span>
            ) : undefined}
            <span className="flex items-center gap-10px">
              {routes.MyTeam && (
                <LinkButton href={routes.MyTeam.path!}>
                  {routes.MyTeam.element}
                </LinkButton>
              )}
              <AnchorButton
                href={routes.SignOut.path}
                variant="outline"
                palette="neutral"
              >
                {routes.SignOut.element}
              </AnchorButton>
            </span>
          </nav>
        }
      />
    </>
  );
}
